import * as THREE from 'three';

const EPS = 1e-9;
const CM_TO_UNIT = 1; 

// --- ML/AP helpers ---
const UP = new THREE.Vector3(0, 1, 0);

function _getWorldPos(mesh, out = new THREE.Vector3()) {
  if (!mesh?.getWorldPosition) return null;
  mesh.getWorldPosition(out);
  return out;
}

export function _computeAxes(points) {
  // Default axes if landmarks are missing
  let uML = new THREE.Vector3(1, 0, 0);
  let uAP = new THREE.Vector3(0, 0, 1);

  const pInt = _getWorldPos(points?.internalCondyle?.sphere, new THREE.Vector3());
  const pExt = _getWorldPos(points?.externalCondyle?.sphere, new THREE.Vector3());

  if (pInt && pExt) {
    const vML = new THREE.Vector3().subVectors(pExt, pInt);
    vML.y = 0; // project to transverse plane
    if (vML.lengthSq() > 1e-10) uML.copy(vML).normalize();

    // AP = Up × ML (in plane)
    uAP.copy(new THREE.Vector3().crossVectors(UP, uML)).normalize();

    // Orient AP toward patella if available
    const pMid = _getWorldPos(points?.midPatellar?.sphere, new THREE.Vector3());
    if (pMid) {
      const midCond = new THREE.Vector3().addVectors(pExt, pInt).multiplyScalar(0.5);
      const vPat = pMid.sub(midCond); vPat.y = 0;
      if (vPat.lengthSq() > 1e-10 && vPat.normalize().dot(uAP) < 0) uAP.multiplyScalar(-1);
    }
  }

  // Fallback if AP degenerate
  if (!isFinite(uAP.x) || !isFinite(uAP.y) || !isFinite(uAP.z) || uAP.lengthSq() < 1e-10) {
    uAP.set(0, 0, 1);
  }
  return { uML, uAP };
}

// Uses the ring geometry built (positions are relative to centroid)
export function _computeMLAPFromBuilt(built, uML, uAP) {
  // Prefer thin (BufferGeometry with .attributes.position); works in all cases
  const posAttr = built?.thin?.geometry?.attributes?.position;
  if (!posAttr?.array || posAttr.count === 0) return { mlM: 0, apM: 0 };

  const arr = posAttr.array; // [x,y,z] relative to centroid (world frame)
  let minML = Infinity, maxML = -Infinity, minAP = Infinity, maxAP = -Infinity;

  // y offsets are ~0 for the slice; ignore y in dot
  for (let i = 0; i < arr.length; i += 3) {
    const x = arr[i], z = arr[i + 2];
    const sML = x * uML.x + z * uML.z;
    const sAP = x * uAP.x + z * uAP.z;
    if (sML < minML) minML = sML;
    if (sML > maxML) maxML = sML;
    if (sAP < minAP) minAP = sAP;
    if (sAP > maxAP) maxAP = sAP;
  }
  return {
    mlM: (maxML - minML) || 0,   // meters
    apM: (maxAP - minAP) || 0,   // meters
  };
}

// after built the slice geometry `built` (or you have the raw intersection points)
// compute centroid in WORLD, then convert to LOCAL for shadedMesh
export function computeSliceCentroidLocalwBuilt(built, shadedMesh, grp) {
  // Try to use the line/segment positions (world) to compute arithmetic mean
  const posAttr = built?.thin?.geometry?.attributes?.position;
  if (!posAttr?.array || posAttr.count === 0) return null;

  // Ensure matrices are up-to-date before calculations
  shadedMesh.updateMatrixWorld(true);
  if (grp) grp.updateMatrixWorld(true);

  const mWorld = grp?.matrixWorld ?? shadedMesh.matrixWorld;
  const v = new THREE.Vector3();
  let sx = 0, sz = 0, c = 0;
  for (let i=0;i<posAttr.array.length;i+=3){
    v.set(posAttr.array[i], posAttr.array[i+1], posAttr.array[i+2]).applyMatrix4(mWorld);
    sx += v.x; sz += v.z; c++;
  }
  if (c === 0) return null;
  const world = new THREE.Vector3(sx/c, 0, sz/c);
  const local = world.clone();
  shadedMesh.worldToLocal(local);
  return { x: local.x, z: local.z };
}

export function computeSliceCentroidLocal(shadedMesh, grp) {
  if (!shadedMesh || !grp) return null;
  // Ensure both matrices are up-to-date before calculations
  shadedMesh.updateMatrixWorld(true);
  grp.updateMatrixWorld(true);
  const w = new THREE.Vector3();
  grp.getWorldPosition(w);                    // ring group is positioned at the slice centroid
  const l = w.clone();
  shadedMesh.worldToLocal(l);                 // convert to shadedMesh local space
  return { x: l.x, z: l.z };
}

//Build a height→scale,centroid LUT from rings
export function buildLUTFromRings(shadedMesh, zeroY, measurements, defaultFalloffCm = 1, profile = 'hann') {
  if (!shadedMesh || !measurements?.length) return null;
  const rows = [];
  const tmp = new THREE.Vector3();

  for (const m of measurements) {
    const yWorld = zeroY + (m.height ?? 0) * CM_TO_UNIT; // cm→m
    const scanC  = m?.scan?.circumference || 0;
    const rectC  = m?.rectified?.circumference || scanC;
    const s      = scanC > 1e-9 ? (rectC / scanC) : 1;

    // Keep yWorld in world space (don't convert to local)
    const Rm = ((m.falloff ?? defaultFalloffCm) * CM_TO_UNIT);

    // centroid (use cached if present, already in local space)
    const cx = m._centroidLocal?.x ?? 0;
    const cz = m._centroidLocal?.z ?? 0;

    // Store world Y coordinate directly
    rows.push({ y: yWorld, scale: s, c: { x: cx, z: cz }, R: Math.max(Rm, 1e-6) });
  }

  rows.sort((a,b)=>a.y - b.y);

  return {
    ys: rows.map(r=>r.y),
    scales: rows.map(r=>r.scale),
    centroids: rows.map(r=>r.c),
    radii: rows.map(r=>r.R),
    profile,
  };
}

function weight01(t, mode='hann'){ // t in [0,1] where t=0 at ring, 1 at band edge
  t = Math.min(Math.max(t, 0), 1);
  switch (mode) {
    case 'hann':      // cosine/Hann, C1 at ends
      return 0.5 * (1 + Math.cos(Math.PI * t));
    case 'smooth5':   // 5th-order smoothstep (C2)
      return 1 - (t*t*t*(t*(t*6 - 15) + 10));
    case 'gauss': {   // Gaussian-ish
      const sigma2 = 0.18; // ~controls width
      return Math.exp(- (t*t) / (2*sigma2));
    }
    default:          // linear fallback
      return 1 - t;
  }
}

//Deform the preview clone (NOT the scan mesh)
export function deformPreviewMesh(previewMesh, lut, basePositions){
  if (!previewMesh || !lut) return;
  const pos = previewMesh.geometry.attributes.position;
  const arr = pos.array;

  // Always start from original to avoid cumulative growth
  if (basePositions && basePositions.length === arr.length) arr.set(basePositions);

  const { ys, scales, centroids, radii, profile='hann' } = lut;
  if (!ys?.length) return;

  // Ensure matrixWorld is up-to-date before using it for calculations
  // This is critical during initialization when the mesh was just added to the scene
  previewMesh.updateMatrixWorld(true);

  // After alignment, geometry is baked and mesh has identity transform
  // So we need to convert local Y to world Y using the mesh transform
  const mw = previewMesh.matrixWorld;
  const mwInv = new THREE.Matrix4().copy(mw).invert();
  const localVec = new THREE.Vector3();
  const worldVec = new THREE.Vector3();
  const worldCentroid = new THREE.Vector3();
  const worldVertex = new THREE.Vector3();

  for (let i=0;i<arr.length;i+=3){
    const x0 = arr[i], y0 = arr[i+1], z0 = arr[i+2];

    // Convert local vertex position to world space
    localVec.set(x0, y0, z0);
    worldVec.copy(localVec).applyMatrix4(mw);
    const yWorld = worldVec.y;  // Use world Y coordinate

    // blend deltas (NOT normalized) so it curves back to 1 at edges
    let delta = 0;          // sum of w * (scale-1)
    let wSum  = 0;          // for centroid averaging only
    let cxSum = 0, czSum = 0;

    for (let k=0;k<ys.length;k++){
      const R = radii[k];
      const d = Math.abs(yWorld - ys[k]);  // Compare world Y coordinates
      if (d >= R) continue;

      const w = weight01(d / R, profile);    // 1 at ring, 0 at edge
      delta += w * (scales[k] - 1);
      wSum  += w;
      cxSum += w * centroids[k].x;
      czSum += w * centroids[k].z;
    }

    if (wSum > 0){
      // Average centroid in local space
      const cxLocal = cxSum / wSum, czLocal = czSum / wSum;
      const s  = Math.max(0.05, 1 + delta);  // clamp small to avoid inversion

      // Convert local centroid to world space using the vertex's world Y
      // We need to find what local Y corresponds to this world Y
      localVec.set(cxLocal, y0, czLocal);  // Use the vertex's local Y temporarily
      localVec.applyMatrix4(mw);

      // Now use the centroid's world X,Z with the vertex's world Y
      worldCentroid.set(localVec.x, yWorld, localVec.z);

      // Apply scaling in world XZ plane (perpendicular to world Y)
      const dx_world = worldVec.x - worldCentroid.x;
      const dz_world = worldVec.z - worldCentroid.z;

      const newWorldX = worldCentroid.x + dx_world * s;
      const newWorldZ = worldCentroid.z + dz_world * s;

      // Convert back to local space
      worldVertex.set(newWorldX, worldVec.y, newWorldZ);  // Keep world Y unchanged
      worldVertex.applyMatrix4(mwInv);

      arr[i  ] = worldVertex.x;
      arr[i+1] = worldVertex.y;
      arr[i+2] = worldVertex.z;
    }
  }

  pos.needsUpdate = true;

  // **CRITICAL FIX**: Update all spatial data structures after geometry modification
  previewMesh.geometry.computeVertexNormals();
  previewMesh.geometry.computeBoundingBox();
  previewMesh.geometry.computeBoundingSphere();

  // **CRITICAL FIX**: Dispose BVH tree if it exists (will be rebuilt when needed)
  // This prevents stale spatial acceleration structures from causing bugs in subsequent tools
  if (previewMesh.geometry.boundsTree) {
    previewMesh.geometry.disposeBoundsTree();
  }
}